import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, MapPin, BarChart3, Users, Target, BookOpen, CheckCircle, ExternalLink, Phone, Lightbulb, TrendingUp, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

function AnimSection({ children, className = '', animation = 'scroll-fade-up' }) {
  const [ref, isVisible] = useScrollReveal(0.12);
  return (
    <div ref={ref} className={`${animation} ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function StaggerGrid({ children, className = '' }) {
  const [ref, isVisible] = useScrollReveal(0.1);
  return (
    <div ref={ref} className={`stagger-children ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function ZipCodeAnalyzer() {
  const [zip, setZip] = useState('');
  const [gpa, setGpa] = useState('');
  const [sat, setSat] = useState('');
  const [result, setResult] = useState(null);

  const analyze = (e) => {
    e.preventDefault();
    if (!zip || zip.length < 5) return;

    const zipNum = parseInt(zip.substring(0, 3));
    let opportunity, context, breakout;

    if (zipNum >= 100 && zipNum <= 102) {
      opportunity = "high";
      context = "Your area has strong access to college prep resources, competitive high schools, and established feeder patterns to selective universities.";
      breakout = "Even in high-opportunity areas, standing out requires strategic positioning. The competition is fierce — everyone has the same advantages. Your story needs to be different.";
    } else if (zipNum >= 900 && zipNum <= 902) {
      opportunity = "high";
      context = "Your area has strong access to college prep resources and established feeder patterns to selective universities.";
      breakout = "High-opportunity areas breed intense competition. Having the same profile as everyone else isn't enough. Strategic narrative and positioning are critical.";
    } else if (zipNum >= 327 && zipNum <= 329) {
      opportunity = "moderate";
      context = "Central Florida has growing resources but historically fewer direct pipelines to Ivy League institutions compared to the Northeast corridor.";
      breakout = "Students from your area are underrepresented at elite institutions — which means admissions officers are looking for you. The right strategy turns geography into an advantage.";
    } else if (zipNum >= 600 && zipNum <= 609) {
      opportunity = "moderate";
      context = "The Chicago metro area has pockets of high opportunity alongside areas with significant barriers. Your specific neighborhood matters enormously.";
      breakout = "Midwest applicants bring a perspective Ivy League schools actively seek. Your story of navigating a complex environment is itself a differentiator.";
    } else {
      opportunity = "variable";
      context = "The Opportunity Atlas shows that outcomes vary dramatically even between neighborhoods just a mile apart. Your ZIP code shapes the resources around you — but not your ceiling.";
      breakout = "Students from areas like yours are exactly who elite institutions want to find. The challenge isn't talent — it's access to the right strategy and positioning.";
    }

    const gpaVal = parseFloat(gpa);
    let gpaNote = "";
    if (gpa && gpaVal) {
      if (gpaVal >= 3.8) gpaNote = "Your GPA puts you in a strong academic position. The question now is narrative — how does your story make you unforgettable?";
      else if (gpaVal >= 3.5) gpaNote = "Your GPA is competitive. With the right positioning, strategic extracurriculars, and essay architecture, you can absolutely compete at the highest level.";
      else if (gpaVal >= 3.0) gpaNote = "Your GPA tells one part of the story. We specialize in building applications where grades are contextualized within a compelling, holistic narrative.";
      else gpaNote = "GPA is one signal among many. Some of the most successful admissions outcomes come from students who demonstrate growth, resilience, and genuine intellectual curiosity.";
    }

    setResult({ opportunity, context, breakout, gpaNote, zip });
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-[#0F392B] overflow-hidden" data-testid="zip-analyzer">
      <div className="bg-[#0F392B] px-6 py-5">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="h-5 w-5 text-[#D4AF37]" />
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>ZIP Code Admissions Reality Analyzer</h3>
        </div>
        <p className="text-xs text-white/60">See what the data says about your area — and what it takes to break out.</p>
      </div>

      <form onSubmit={analyze} className="p-6 space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Your ZIP Code *</label>
          <input
            type="text"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="e.g. 32837"
            className="w-full px-3 py-2.5 text-sm border border-[#E2E2E2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20 focus:border-[#0F392B]"
            required
            data-testid="zip-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">GPA (optional)</label>
            <input
              type="text"
              value={gpa}
              onChange={(e) => setGpa(e.target.value)}
              placeholder="e.g. 3.7"
              className="w-full px-3 py-2.5 text-sm border border-[#E2E2E2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20 focus:border-[#0F392B]"
              data-testid="gpa-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">SAT Score (optional)</label>
            <input
              type="text"
              value={sat}
              onChange={(e) => setSat(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 1450"
              className="w-full px-3 py-2.5 text-sm border border-[#E2E2E2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20 focus:border-[#0F392B]"
              data-testid="sat-input"
            />
          </div>
        </div>
        <Button type="submit" className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11" data-testid="zip-analyze-btn">
          Analyze My Area <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </form>

      {result && (
        <div className="px-6 pb-6 space-y-4" data-testid="zip-result">
          <div className="border-t border-[#E2E2E2] pt-4">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full ${result.opportunity === 'high' ? 'bg-emerald-500' : result.opportunity === 'moderate' ? 'bg-amber-500' : 'bg-blue-500'}`} />
              <p className="text-sm font-medium">
                ZIP {result.zip} — {result.opportunity === 'high' ? 'High' : result.opportunity === 'moderate' ? 'Moderate' : 'Variable'} Opportunity Area
              </p>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{result.context}</p>
            <div className="bg-[#0F392B]/5 rounded-xl p-4 mb-3">
              <p className="text-xs font-medium text-[#0F392B] mb-1">What It Takes to Break Out:</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{result.breakout}</p>
            </div>
            {result.gpaNote && (
              <div className="bg-[#D4AF37]/10 rounded-xl p-4 mb-3">
                <p className="text-xs font-medium text-[#D4AF37] mb-1">Your Academic Profile:</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{result.gpaNote}</p>
              </div>
            )}
            <Link to="/premium">
              <Button className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#0F392B] h-11 font-semibold mt-2" data-testid="zip-cta">
                Build My Strategy <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ZipCodePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="zipcode-page">
      <SEO
        title="Your ZIP Code Is Not Your Destiny — Ivy League Consulting"
        description="The data shows that where you grow up shapes where you end up. But it doesn't have to. Ivy League Consulting is the intervention layer that breaks the pattern. Explore the Opportunity Atlas and build your strategy."
        path="/zip-code"
      />
      <MarketingNav />

      {/* SECTION 1: Hero / Hook */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F392B]/5 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="max-w-3xl">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">The Data Behind Opportunity</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Where You Grow Up Shapes<br />Where You End Up —<br />
              <span className="italic text-[#0F392B]">But It Doesn't Have To.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4 max-w-2xl">
              The <a href="https://www.opportunityatlas.org" target="_blank" rel="noopener noreferrer" className="text-[#0F392B] font-medium underline underline-offset-2 hover:text-[#D4AF37] transition-colors">Opportunity Atlas</a> — a study tracking <span className="text-foreground font-medium">20 million Americans over time</span> — proves that childhood neighborhoods strongly influence adult income, education, and life outcomes. This is not theory. It's data.
            </p>
            <div className="bg-[#0F392B] rounded-xl p-5 inline-block">
              <p className="text-base text-white font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                "We don't accept ZIP codes as limits.<br />We treat them as starting points."
              </p>
              <p className="text-xs text-[#D4AF37] mt-2">— Ivy League Consulting</p>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* SECTION 2: What the Data Shows */}
      <section className="py-20 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="data-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="text-center mb-14">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">What the Data Actually Shows</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Same talent. <span className="italic">Different outcomes.</span>
            </h2>
          </AnimSection>
          <StaggerGrid className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-[#0F392B] flex items-center justify-center mb-4">
                <MapPin className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <p className="text-sm font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Neighborhoods a Mile Apart</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Children's outcomes vary <span className="text-foreground font-medium">dramatically</span> even between neighborhoods just one mile apart. Same city. Same school district. Wildly different futures.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-[#0F392B] flex items-center justify-center mb-4">
                <BarChart3 className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <p className="text-sm font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Tracked by Real Outcomes</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The Atlas tracks income, incarceration rates, and economic mobility by neighborhood. It's the most comprehensive map of <span className="text-foreground font-medium">American opportunity</span> ever built.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-[#0F392B] flex items-center justify-center mb-4">
                <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <p className="text-sm font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Thousands of Dollars</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Where you grow up can influence your adult earnings by <span className="text-foreground font-medium">thousands of dollars annually</span>. The pattern is clear — and it's not about talent.
              </p>
            </div>
          </StaggerGrid>
          <AnimSection className="text-center">
            <div className="inline-block bg-[#0F392B] rounded-xl px-8 py-5">
              <p className="text-xl md:text-2xl font-medium text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                "Talent is evenly distributed. <span className="italic text-[#D4AF37]">Opportunity is not.</span>"
              </p>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* SECTION 3: Opportunity Atlas + ZIP Tool */}
      <section className="py-20 md:py-28" data-testid="tool-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="text-center mb-14">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Explore the Data</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              See what your ZIP code <span className="italic">really means.</span>
            </h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Enter your ZIP code below. Then imagine applying to Harvard from there — without guidance.
            </p>
          </AnimSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left: Opportunity Atlas Link */}
            <AnimSection animation="scroll-fade-left">
              <div className="bg-[#F9F9F7] rounded-2xl border border-[#E2E2E2] p-6 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <img src="https://www.opportunityatlas.org/favicon.ico" alt="" className="w-5 h-5" onError={(e) => e.target.style.display='none'} />
                  <p className="text-sm font-medium">The Opportunity Atlas</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Explore the interactive map built from data on 20 million Americans. Search by neighborhood, income, race, and gender to see how outcomes vary across the country.
                </p>
                <a href="https://www.opportunityatlas.org" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white h-11" data-testid="atlas-link-btn">
                    Open Opportunity Atlas <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>

              {/* Callout Box */}
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">Try this:</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Enter your ZIP code on the Opportunity Atlas. Look at the adult income outcomes. Now imagine applying to Harvard from there — <span className="text-foreground font-medium italic">without guidance.</span>
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                      That's the gap we close. That's what Ivy League Consulting exists for.
                    </p>
                  </div>
                </div>
              </div>

              {/* Split screen visual */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <p className="text-[10px] font-medium text-red-600 uppercase tracking-widest mb-1">Low Opportunity ZIP</p>
                  <p className="text-2xl font-bold text-red-700" style={{ fontFamily: "'Playfair Display', serif" }}>$26K</p>
                  <p className="text-[10px] text-red-500">avg. adult income</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-widest mb-1">High Opportunity ZIP</p>
                  <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Playfair Display', serif" }}>$78K</p>
                  <p className="text-[10px] text-emerald-500">avg. adult income</p>
                </div>
              </div>
              <p className="text-center text-xs text-muted-foreground mt-2 italic">Same talent. Different outcomes.</p>
            </AnimSection>

            {/* Right: ZIP Code Analyzer */}
            <AnimSection animation="scroll-fade-right">
              <ZipCodeAnalyzer />
            </AnimSection>
          </div>
        </div>
      </section>

      {/* SECTION 4: Bridge — "This Is Where We Come In" */}
      <section className="py-20 bg-[#0F392B]" data-testid="bridge-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">This Is Where We Come In</p>
                <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  The Atlas shows where<br />opportunity exists.<br />
                  <span className="italic text-[#D4AF37]">We build how to access it.</span>
                </h2>
                <p className="text-base text-white/70 leading-relaxed mb-6">
                  Data doesn't change outcomes. <span className="text-white font-medium">Strategy does.</span> We take everything the Opportunity Atlas reveals — the structural barriers, the geographic disadvantages, the uneven playing field — and we build a path through it.
                </p>
                <Link to="/premium">
                  <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#0F392B] h-12 px-8 font-semibold" data-testid="bridge-cta">
                    Build My Strategy <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <StaggerGrid className="space-y-4">
                {[
                  { icon: Target, title: "Structured Application Strategy", desc: "Every school, every essay, every deadline — architected with precision." },
                  { icon: BookOpen, title: "Story Positioning", desc: "We turn where you're from into the most compelling part of your application." },
                  { icon: Users, title: "Mentorship + Network Access", desc: "Exclusive events, alumni connections, real intellectual exposure." },
                  { icon: GraduationCap, title: "Exposure & Positioning", desc: "We don't just open doors. We make sure you walk through them prepared." },
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10 flex items-start gap-4 hover:bg-white/15 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{item.title}</p>
                      <p className="text-xs text-white/60 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </StaggerGrid>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* SECTION 5: Differentiator */}
      <section className="py-20 md:py-28" data-testid="differentiator-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection className="text-center mb-14">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Our Positioning</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              We don't just help students apply.<br />
              We <span className="italic">reposition</span> them.
            </h2>
          </AnimSection>
          <StaggerGrid className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "Local Context → Compelling Narrative", desc: "Where you're from becomes the foundation of a story admissions officers remember." },
              { icon: Shield, title: "Background → Competitive Advantage", desc: "Obstacles become evidence of resilience, maturity, and determination." },
              { icon: Zap, title: "Any Origin → Elite Positioning", desc: "We build Ivy-level applications regardless of ZIP code, school name, or starting point." },
            ].map((item, i) => (
              <div key={i} className="bg-[#F9F9F7] rounded-2xl border border-[#E2E2E2] p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-[#0F392B] flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* SECTION 6: Signature Line + CTA */}
      <section className="py-20 bg-[#0F392B] relative" data-testid="signature-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              No one is overqualified.<br />
              No one should ever feel<br />
              <span className="italic text-[#D4AF37]">underqualified.</span>
            </h2>
            <p className="text-lg text-white/70 mt-6 mb-10">
              Pick up the phone. Let's talk about your future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:2027313100">
                <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-14 px-10 text-base font-semibold shadow-xl" data-testid="signature-call-cta">
                  <Phone className="mr-2 h-5 w-5" /> Call (202) 731-3100
                </Button>
              </a>
              <Link to="/premium">
                <Button size="lg" variant="outline" className="h-14 px-10 text-base border-white/25 text-white hover:bg-white/10" data-testid="signature-strategy-cta">
                  Build My Strategy <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <p className="text-xs text-white/30 mt-6">24/7 On Call Staff — taimur@ivyleaguehelp.com</p>
          </AnimSection>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
